import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    calculateRow,
    CalculatorInputs,
    CalculatorOutputs,
    CalculatorMetricId,
} from '@/utils/score-engine';
import { findMetricId } from './utils';

export interface TestStatisticsModalHandle {
    apply: () => void;
    canApply: boolean;
}

interface TestStatisticsModalProps {
    initialData?: Record<string, string>;
    availableColumns: { id: string; name: string }[];
    onApply: (updates: Record<string, string>) => void;
    onClose: () => void;
}

// Input fields (what the user types)
const INPUT_FIELDS: {
    id: CalculatorMetricId;
    label: string;
    placeholder: string;
}[] = [
    { id: 'raw', label: 'Resultado Bruto (X)', placeholder: '0' },
    { id: 'mean', label: 'Média (μ)', placeholder: '0' },
    { id: 'sd', label: 'Desvio Padrão (σ)', placeholder: '0' },
];

// Output score types that can appear as cards (excludes classification — shown separately)
const OUTPUT_SCORE_IDS: {
    id: CalculatorMetricId;
    label: string;
}[] = [
    { id: 'z', label: 'Escore Z' },
    { id: 't', label: 'Escore T' },
    { id: 'iq', label: 'QI (Escore Padrão)' },
    { id: 'scaled10_3', label: 'Escala 10/3' },
    { id: 'percentile', label: 'Percentil' },
    { id: 'sten', label: 'Sten' },
    { id: 'stanine', label: 'Stanine' },
];

// All field IDs that the engine may produce (used for input clearing logic)
const ALL_OUTPUT_IDS: CalculatorMetricId[] = [
    'z',
    't',
    'iq',
    'scaled10_3',
    'percentile',
    'sten',
    'stanine',
    'classification',
];
const ALL_INPUT_IDS: CalculatorMetricId[] = ['raw'];
const ALL_META_IDS: CalculatorMetricId[] = ['mean', 'sd'];

export const TestStatisticsModal = forwardRef<TestStatisticsModalHandle, TestStatisticsModalProps>(
    function TestStatisticsModal(
        { initialData = {}, availableColumns, onApply, onClose: _onClose },
        ref,
    ) {
        // ── State ────────────────────────────────────────────────────────────
        const getInitialState = (): CalculatorInputs => {
            const state: CalculatorInputs = {};
            Object.entries(initialData).forEach(([colName, value]) => {
                const mappedId = findMetricId(colName);
                if (mappedId) {
                    state[mappedId] = value;
                }
            });
            return state;
        };

        const [inputs, setInputs] = useState<CalculatorInputs>(getInitialState());
        const [calculated, setCalculated] = useState<CalculatorOutputs>({});

        // ── Calculation Effect ───────────────────────────────────────────────
        useEffect(() => {
            const results = calculateRow(inputs);
            setCalculated(results);
        }, [inputs]);

        // ── Input Handlers ───────────────────────────────────────────────────
        const handleChange = (id: CalculatorMetricId, value: string) => {
            setInputs((prev) => {
                const next = { ...prev, [id]: value };

                const isInput = ALL_INPUT_IDS.includes(id);
                const isMeta = ALL_META_IDS.includes(id);
                const isOutput = ALL_OUTPUT_IDS.includes(id);

                // If Input (Raw) or Meta (Mean/SD) changed → clear Outputs
                if (isInput || isMeta) {
                    ALL_OUTPUT_IDS.forEach((outId) => {
                        delete next[outId];
                    });
                }

                // If Output changed → clear other Outputs AND Raw
                if (isOutput) {
                    ALL_OUTPUT_IDS.filter((outId) => outId !== id).forEach((outId) => {
                        delete next[outId];
                    });
                    ALL_INPUT_IDS.forEach((inId) => {
                        delete next[inId];
                    });
                }

                return next;
            });
        };

        // ── Determine which output scores to show as cards ───────────────────
        // Only show scores that have matching columns in the table
        const visibleScoreCards = useMemo(() => {
            const columnMetricIds = new Set<CalculatorMetricId>();
            availableColumns.forEach((col) => {
                const metricId = findMetricId(col.name);
                if (metricId) columnMetricIds.add(metricId);
            });

            return OUTPUT_SCORE_IDS.filter((score) => columnMetricIds.has(score.id));
        }, [availableColumns]);

        // Check if classification column exists in table
        const hasClassificationColumn = useMemo(() => {
            return availableColumns.some((col) => {
                const metricId = findMetricId(col.name);
                return metricId === 'classification';
            });
        }, [availableColumns]);

        // ── Affected columns (for enabling the apply button) ─────────────────
        const affectedColumns = useMemo(() => {
            const affected: string[] = [];
            availableColumns.forEach((col) => {
                const mappedId = findMetricId(col.name);
                if (mappedId && calculated[mappedId] !== undefined) {
                    affected.push(col.name);
                }
            });
            return affected;
        }, [availableColumns, calculated]);

        // ── Apply Logic ──────────────────────────────────────────────────────
        const buildUpdates = (): Record<string, string> => {
            const updates: Record<string, string> = {};
            availableColumns.forEach((col) => {
                const mappedId = findMetricId(col.name);
                if (mappedId) {
                    const val =
                        calculated[mappedId] !== undefined && calculated[mappedId] !== null
                            ? calculated[mappedId]
                            : inputs[mappedId];

                    if (val !== undefined && val !== null) {
                        updates[col.id] = String(val);
                    }
                }
            });
            return updates;
        };

        // ── Imperative Handle for parent footer ──────────────────────────────
        useImperativeHandle(
            ref,
            () => ({
                apply: () => {
                    onApply(buildUpdates());
                },
                canApply: affectedColumns.length > 0,
            }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [affectedColumns, calculated, inputs, availableColumns, onApply],
        );

        // ── Helpers ──────────────────────────────────────────────────────────
        const getDisplayValue = (id: CalculatorMetricId): string => {
            // User-typed value takes precedence
            if (inputs[id] !== undefined && inputs[id] !== '') {
                return String(inputs[id]);
            }
            // Fall back to calculated
            if (calculated[id] !== undefined && calculated[id] !== null) {
                return String(calculated[id]);
            }
            return '';
        };

        const hasResults = !calculated._error && calculated._source && visibleScoreCards.length > 0;

        const classificationValue = getDisplayValue('classification');

        // ── Render ───────────────────────────────────────────────────────────
        return (
            <div className="flex flex-col gap-6">
                {/* ── Entrada de dados ─────────────────────────────────── */}
                <div className="flex flex-col gap-3">
                    <span className="text-xs font-medium text-muted-foreground">
                        Entrada de dados
                    </span>
                    <div className="grid grid-cols-3 gap-4">
                        {INPUT_FIELDS.map((field) => (
                            <div key={field.id} className="flex flex-col gap-1.5">
                                <Label
                                    htmlFor={`calc-${field.id}`}
                                    className="text-xs font-semibold text-foreground"
                                >
                                    {field.label}
                                </Label>
                                <Input
                                    id={`calc-${field.id}`}
                                    value={inputs[field.id] ?? ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleChange(field.id, e.target.value)
                                    }
                                    placeholder={field.placeholder}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Resultados calculados ────────────────────────────── */}
                <div className="flex flex-col gap-3">
                    <span className="text-xs font-medium text-muted-foreground">
                        Resultados calculados
                    </span>

                    {calculated._error ? (
                        <div className="rounded-xl border border-border bg-surface-muted px-4 py-6 text-center text-sm text-muted-foreground">
                            {calculated._error}
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-xl border border-border bg-surface-muted">
                            {/* Score Cards Grid */}
                            {visibleScoreCards.length > 0 && (
                                <div
                                    className="grid divide-x divide-border"
                                    style={{
                                        gridTemplateColumns: `repeat(${visibleScoreCards.length}, minmax(0, 1fr))`,
                                    }}
                                >
                                    {visibleScoreCards.map((score) => {
                                        const value = getDisplayValue(score.id);
                                        return (
                                            <div
                                                key={score.id}
                                                className="flex flex-col items-center gap-1 px-3 py-4"
                                            >
                                                <span className="text-[11px] font-medium text-muted-foreground">
                                                    {score.label}
                                                </span>
                                                <span className="text-3xl font-bold tabular-nums text-foreground">
                                                    {value || '—'}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Classification */}
                            {hasClassificationColumn && (
                                <div className="flex flex-col items-center gap-1 border-t border-border px-4 py-4">
                                    <span className="text-[11px] font-medium text-muted-foreground">
                                        Classificação Final
                                    </span>
                                    <span className="text-lg font-bold text-foreground">
                                        {classificationValue || '—'}
                                    </span>
                                </div>
                            )}

                            {/* Empty state when no results yet but no error either */}
                            {!hasResults && !calculated._error && (
                                <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                                    Preencha os dados acima para calcular os escores.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Disclaimer ──────────────────────────────────────── */}
                <p className="text-xs leading-relaxed text-muted-foreground">
                    O cálculo é baseado na distribuição normal padrão. Verifique os dados antes de
                    aplicar.
                </p>
            </div>
        );
    },
);
